import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { Card } from '@/components/ui/card';
import { gbp } from '@/components/sales/SalesOrderDialog';

const mondayOf = (isoDate) => { const d = new Date(`${isoDate}T12:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };
const isoOf = (d) => d.toISOString().slice(0, 10);
const label = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** Bar per week (Mon–Sun) across the selected range; weeks with no orders still appear as £0 so gaps are visible. */
export function WeeklyProfitChart({ orders, from, to, inc }) {
  const data = useMemo(() => {
    const weeks = new Map();
    for (let d = mondayOf(from); d <= new Date(`${to}T12:00:00`); d.setDate(d.getDate() + 7)) {
      const k = isoOf(d);
      weeks.set(k, { key: k, label: `w/c ${label(d)}`, profit: 0, sales: 0, costs: 0, count: 0 });
    }
    for (const o of orders || []) {
      const w = weeks.get(isoOf(mondayOf(o.date)));
      if (!w) continue;
      w.count += 1;
      w.profit += inc ? o.profit : o.profit_net;
      w.sales += inc ? o.sale_gross : o.sale_net;
      w.costs += inc ? o.costs_total : o.costs_total_net;
    }
    return [...weeks.values()].map((w) => ({ ...w, profit: Math.round(w.profit * 100) / 100 }));
  }, [orders, from, to, inc]);

  if (data.length < 2) return null;
  const best = Math.max(...data.map((d) => d.profit));

  return (
    <Card className="p-4" data-testid="sales-weekly-chart">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-bold text-gray-900">Profit by week <span className="text-xs font-normal text-gray-500">(ex VAT · weeks start Monday)</span></h2>
        <span className="text-xs text-gray-500">{data.length} weeks</span>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={data.length > 16 ? Math.ceil(data.length / 16) - 1 : 0} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${Math.abs(v) >= 1000 ? `${Math.round(v / 100) / 10}k` : v}`} width={56} />
            <ReferenceLine y={0} stroke="#9ca3af" />
            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={({ active, payload }) => (active && payload?.length ? <ChartTip w={payload[0].payload} /> : null)} />
            <Bar dataKey="profit" radius={[4, 4, 0, 0]} maxBarSize={56}>
              {data.map((d) => <Cell key={d.key} fill={d.profit < 0 ? '#be123c' : d.profit === best && best > 0 ? '#D4A24B' : '#1f4030'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function ChartTip({ w }) {
  return (
    <div className="bg-white border rounded-lg shadow-lg px-3 py-2 text-xs space-y-0.5">
      <div className="font-bold text-gray-900">{w.label}</div>
      <div className="text-gray-600">{w.count} order{w.count === 1 ? '' : 's'} · sold {gbp(w.sales)} · costs {gbp(w.costs)}</div>
      <div className={`font-bold ${w.profit < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>Profit {gbp(w.profit)}</div>
    </div>
  );
}
